import React from 'react';
import { CircularProgress, Stack, styled, useTheme } from '@mui/material';
import { useNavigate } from 'react-router';
import { ContainerLayout } from 'components/ContainerLayout/ContainerLayout';
import { PageHeader } from 'components/PageHeader/PageHeader';
import { BenefitCard } from 'components/BenefitCard/BenefitCard';
import { BenefitDetailPage } from 'components/BenefitDetailPage/BenefitDetailPage';
import { EmptyState } from 'components/EmptyState/EmptyState';
import { ErrorState } from 'components/ErrorState/ErrorState';
import { RetryButton } from 'components/RetryButton/RetryButton';
import { useInfiniteScroll } from 'hooks/useInfiniteScroll';
import type { UseBenefitsResult } from 'features/resources/hooks/useBenefits';
import { createImagePrefetcher, toBenefitDetail } from 'features/resources/utils/benefitsUtils';
import { useTranslationContext } from 'contexts/TranslationContext/TranslationContext';
import NetworkError from 'assets/images/ErrorState/NetworkError.svg';
import NoArticles from 'assets/images/EmptyState/NoArticles.svg';
import { ResourceCardGrid, ResourceCardGridCell } from './ResourceCardGrid';
import { ResourceSectionFrame } from './ResourceSectionFrame';

const PageColumn = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  minHeight: 0,
  backgroundColor: theme.palette.brandBackgroundPrimary.main,
}));

const ScrollArea = styled('div')(() => ({
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
}));

const CardsFrame = styled(ResourceSectionFrame)(({ theme }) => ({
  paddingBlock: theme.spacing(2),
  paddingBottom: 0,
}));

const NextPageLoader = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: 40,
  marginBottom: theme.spacing(13),
}));

const prefetchLargeImage = createImagePrefetcher();

const CenteredState = styled(Stack)(() => ({
  height: '100%',
  justifyContent: 'center',
  alignItems: 'center',
}));

export type BenefitsPageContentProps = UseBenefitsResult;

export const BenefitsPageContent: React.FC<BenefitsPageContentProps> = ({
  benefits,
  hasMore,
  loadNextPage,
  retry,
  isLoading,
  isFetching,
  isError,
  selectedBenefit,
  isModalOpen,
  closeModal,
  handleBenefitCardClick,
}) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { translation } = useTranslationContext();
  const sentinelRef = useInfiniteScroll(loadNextPage);

  const goBack = () => navigate(-1);

  const renderContent = () => {
    // Only the initial load blanks the page; `isFetching` during pagination keeps the grid visible.
    if (isLoading) {
      return (
        <CenteredState role="status" aria-live="polite">
          <CircularProgress aria-label="loading" />
        </CenteredState>
      );
    }

    // A failed later page keeps the loaded grid and offers a retry row below it instead.
    if (isError && benefits.length === 0) {
      return (
        <ErrorState
          imageArray={[NetworkError]}
          title={translation('member_resources_benefits_loading_error_title')}
          subtitle={translation('error_loading_body')}
          onRetryClick={retry}
        />
      );
    }

    if (benefits.length === 0) {
      return (
        <EmptyState
          imageArray={[NoArticles]}
          title={translation('member_resources_benefits_loading_error_title')}
          subtitle=""
          style={{ height: '60%', padding: 16 }}
        />
      );
    }

    return (
      <ScrollArea>
        <CardsFrame>
          <ResourceCardGrid>
            {benefits.map((benefit) => (
              <ResourceCardGridCell
                key={benefit.id}
                onPointerEnter={() => prefetchLargeImage(benefit.largerImageUrl)}
                onPointerDown={() => prefetchLargeImage(benefit.largerImageUrl)}
                onFocus={() => prefetchLargeImage(benefit.largerImageUrl)}
              >
                <BenefitCard
                  title={benefit.title}
                  imageUrl={benefit.thumbnailImageUrl}
                  backgroundColor={theme.palette.background.default}
                  variant="square"
                  onClick={() => handleBenefitCardClick(benefit)}
                  selected={selectedBenefit?.id === benefit.id}
                  ariaLabel={benefit.title}
                />
              </ResourceCardGridCell>
            ))}
          </ResourceCardGrid>
        </CardsFrame>
        <NextPageLoader
          role="status"
          aria-live="polite"
          ref={hasMore && !isError ? sentinelRef : undefined}
        >
          {isError ? (
            <RetryButton onClick={retry} />
          ) : (
            isFetching && <CircularProgress size={28} aria-label="loading" />
          )}
        </NextPageLoader>
      </ScrollArea>
    );
  };

  return (
    <ContainerLayout>
      <PageColumn>
        <PageHeader
          isBackVisible
          title={translation('resources_benefits_header')}
          onBack={goBack}
        />
        {renderContent()}
        <BenefitDetailPage
          benefit={selectedBenefit ? toBenefitDetail(selectedBenefit) : undefined}
          open={isModalOpen}
          onClose={closeModal}
        />
      </PageColumn>
    </ContainerLayout>
  );
};
